import { c as _c } from "react/compiler-runtime";
import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { Menubar as MenubarPrimitive } from '@base-ui/react/menubar';
import { CheckIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { CircleIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';
type PositionerProps = Pick<React.ComponentProps<typeof MenuPrimitive.Positioner>, 'align' | 'alignOffset' | 'collisionBoundary' | 'collisionPadding' | 'side' | 'sideOffset' | 'sticky'>;
type SelectCompatProps = {
  onSelect?: React.MouseEventHandler<HTMLElement>;
};
function Menubar(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex h-9 items-center gap-1 rounded-md border bg-background p-1 shadow-xs", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <MenubarPrimitive data-slot="menubar" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function MenubarMenu(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <MenuPrimitive.Root data-slot="menubar-menu" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function MenubarGroup(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <MenuPrimitive.Group data-slot="menubar-group" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function MenubarPortal(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <MenuPrimitive.Portal data-slot="menubar-portal" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function MenubarRadioGroup(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <MenuPrimitive.RadioGroup data-slot="menubar-radio-group" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function MenubarTrigger(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex items-center rounded-sm px-2 py-1 text-sm font-medium outline-hidden select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-popup-open:bg-accent data-popup-open:text-accent-foreground", "sq:rounded-[.75rem]", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <MenuPrimitive.Trigger data-slot="menubar-trigger" className={t1} suppressHydrationWarning={true} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function MenubarContent(t0) {
  const $ = _c(24);
  let className;
  let collisionBoundary;
  let collisionPadding;
  let props;
  let side;
  let sticky;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      align: t1,
      alignOffset: t2,
      sideOffset: t3,
      collisionBoundary,
      collisionPadding,
      side,
      sticky,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = collisionBoundary;
    $[3] = collisionPadding;
    $[4] = props;
    $[5] = side;
    $[6] = sticky;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    className = $[1];
    collisionBoundary = $[2];
    collisionPadding = $[3];
    props = $[4];
    side = $[5];
    sticky = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  const align = t1 === undefined ? "start" : t1;
  const alignOffset = t2 === undefined ? -4 : t2;
  const sideOffset = t3 === undefined ? 8 : t3;
  let t4;
  if ($[10] !== className) {
    t4 = cn("z-50 min-w-48 origin-(--transform-origin) overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", "[--sq-r:1rem] sq:relative sq:rounded-(--sq-r) sq:shadow-none sq:drop-shadow-md", className);
    $[10] = className;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props || $[13] !== t4) {
    t5 = <MenuPrimitive.Popup data-slot="menubar-content" className={t4} {...props} />;
    $[12] = props;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== align || $[16] !== alignOffset || $[17] !== collisionBoundary || $[18] !== collisionPadding || $[19] !== side || $[20] !== sideOffset || $[21] !== sticky || $[22] !== t5) {
    t6 = <MenubarPortal><MenuPrimitive.Positioner align={align} alignOffset={alignOffset} collisionBoundary={collisionBoundary} collisionPadding={collisionPadding} side={side} sideOffset={sideOffset} sticky={sticky}>{t5}</MenuPrimitive.Positioner></MenubarPortal>;
    $[15] = align;
    $[16] = alignOffset;
    $[17] = collisionBoundary;
    $[18] = collisionPadding;
    $[19] = side;
    $[20] = sideOffset;
    $[21] = sticky;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}
function MenubarItem(t0) {
  const $ = _c(20);
  let className;
  let closeOnClick;
  let inset;
  let onClick;
  let onSelect;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      variant: t1,
      onSelect,
      onClick,
      closeOnClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = closeOnClick;
    $[3] = inset;
    $[4] = onClick;
    $[5] = onSelect;
    $[6] = props;
    $[7] = t1;
  } else {
    className = $[1];
    closeOnClick = $[2];
    inset = $[3];
    onClick = $[4];
    onSelect = $[5];
    props = $[6];
    t1 = $[7];
  }
  const variant = t1 === undefined ? "default" : t1;
  const t2 = closeOnClick ?? (onSelect ? false : undefined);
  let t3;
  if ($[8] !== className) {
    t3 = cn("relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-accent data-highlighted:text-accent-foreground data-inset:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:data-highlighted:bg-destructive/10 data-[variant=destructive]:data-highlighted:text-destructive dark:data-[variant=destructive]:data-highlighted:bg-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive!", className);
    $[8] = className;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== onClick || $[11] !== onSelect) {
    t4 = event => {
      onSelect?.(event);
      onClick?.(event);
    };
    $[10] = onClick;
    $[11] = onSelect;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== inset || $[14] !== props || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== variant) {
    t5 = <MenuPrimitive.Item data-slot="menubar-item" data-inset={inset} data-variant={variant} closeOnClick={t2} className={t3} onClick={t4} {...props} />;
    $[13] = inset;
    $[14] = props;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = variant;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
function MenubarCheckboxItem(t0) {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex cursor-default items-center gap-2 rounded-xs py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-accent data-highlighted:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center"><MenuPrimitive.CheckboxItemIndicator><CheckIcon className="size-4" /></MenuPrimitive.CheckboxItemIndicator></span>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t1) {
    t3 = <MenuPrimitive.CheckboxItem data-slot="menubar-checkbox-item" className={t1} {...props}>{t2}{children}</MenuPrimitive.CheckboxItem>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function MenubarRadioItem(t0) {
  const $ = _c(17);
  let children;
  let className;
  let onClick;
  let onSelect;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      onSelect,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onClick;
    $[4] = onSelect;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    onClick = $[3];
    onSelect = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cn("relative flex cursor-default items-center gap-2 rounded-xs py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-accent data-highlighted:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", "[--sq-r:.5rem] sq:rounded-(--sq-r)", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== onClick || $[9] !== onSelect) {
    t2 = event => {
      onSelect?.(event);
      onClick?.(event);
    };
    $[8] = onClick;
    $[9] = onSelect;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center"><MenuPrimitive.RadioItemIndicator><CircleIcon className="size-2 fill-current" /></MenuPrimitive.RadioItemIndicator></span>;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== children || $[13] !== props || $[14] !== t1 || $[15] !== t2) {
    t4 = <MenuPrimitive.RadioItem data-slot="menubar-radio-item" className={t1} onClick={t2} {...props}>{t3}{children}</MenuPrimitive.RadioItem>;
    $[12] = children;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
function MenubarLabel(t0) {
  const $ = _c(10);
  let className;
  let inset;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = inset;
    $[3] = props;
  } else {
    className = $[1];
    inset = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("px-2 py-1.5 text-sm font-medium data-inset:pl-8", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== inset || $[7] !== props || $[8] !== t1) {
    t2 = <div data-slot="menubar-label" data-inset={inset} className={t1} {...props} />;
    $[6] = inset;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function MenubarSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <MenuPrimitive.Separator data-slot="menubar-separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function MenubarShortcut(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("ml-auto text-xs tracking-[.1em] text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span data-slot="menubar-shortcut" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function MenubarSub(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <MenuPrimitive.SubmenuRoot data-slot="menubar-sub" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function MenubarSubTrigger(t0) {
  const $ = _c(13);
  let children;
  let className;
  let inset;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = inset;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    inset = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-inset:pl-8 data-popup-open:bg-accent data-popup-open:text-accent-foreground", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronRightIcon className="ml-auto h-4 w-4" />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== inset || $[10] !== props || $[11] !== t1) {
    t3 = <MenuPrimitive.SubmenuTrigger data-slot="menubar-sub-trigger" data-inset={inset} className={t1} {...props}>{children}{t2}</MenuPrimitive.SubmenuTrigger>;
    $[8] = children;
    $[9] = inset;
    $[10] = props;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function MenubarSubContent(t0) {
  const $ = _c(20);
  let alignOffset;
  let className;
  let collisionBoundary;
  let collisionPadding;
  let props;
  let sideOffset;
  let sticky;
  if ($[0] !== t0) {
    ({
      className,
      alignOffset,
      collisionBoundary,
      collisionPadding,
      sideOffset,
      sticky,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alignOffset;
    $[2] = className;
    $[3] = collisionBoundary;
    $[4] = collisionPadding;
    $[5] = props;
    $[6] = sideOffset;
    $[7] = sticky;
  } else {
    alignOffset = $[1];
    className = $[2];
    collisionBoundary = $[3];
    collisionPadding = $[4];
    props = $[5];
    sideOffset = $[6];
    sticky = $[7];
  }
  let t1;
  if ($[8] !== className) {
    t1 = cn("z-50 min-w-32 origin-(--transform-origin) overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", className);
    $[8] = className;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== props || $[11] !== t1) {
    t2 = <MenuPrimitive.Popup data-slot="menubar-sub-content" className={t1} {...props} />;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== alignOffset || $[14] !== collisionBoundary || $[15] !== collisionPadding || $[16] !== sideOffset || $[17] !== sticky || $[18] !== t2) {
    t3 = <MenubarPortal><MenuPrimitive.Positioner alignOffset={alignOffset} collisionBoundary={collisionBoundary} collisionPadding={collisionPadding} sideOffset={sideOffset} sticky={sticky}>{t2}</MenuPrimitive.Positioner></MenubarPortal>;
    $[13] = alignOffset;
    $[14] = collisionBoundary;
    $[15] = collisionPadding;
    $[16] = sideOffset;
    $[17] = sticky;
    $[18] = t2;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  return t3;
}
export { Menubar, MenubarCheckboxItem, MenubarContent, MenubarGroup, MenubarItem, MenubarLabel, MenubarMenu, MenubarPortal, MenubarRadioGroup, MenubarRadioItem, MenubarSeparator, MenubarShortcut, MenubarSub, MenubarSubContent, MenubarSubTrigger, MenubarTrigger };